/**
 * AccessibilityToolkit - Stories 24.81-24.83
 * Accessibility features and ARIA enhancements
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  Eye, Volume2, VolumeX, Minus, Plus, Type, Contrast,
  Settings, X, Check, Sun, Moon, Zap, RefreshCw
} from 'lucide-react';
import { createPortal } from 'react-dom';

// Accessibility context
interface A11ySettings {
  fontSize: 'normal' | 'large' | 'x-large';
  contrast: 'normal' | 'high';
  reducedMotion: boolean;
  screenReaderMode: boolean;
  colorBlindMode: 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';
}

interface A11yContextType {
  settings: A11ySettings;
  updateSettings: (updates: Partial<A11ySettings>) => void;
  resetSettings: () => void;
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
}

const defaultSettings: A11ySettings = {
  fontSize: 'normal',
  contrast: 'normal',
  reducedMotion: false,
  screenReaderMode: false,
  colorBlindMode: 'none'
};

const A11yContext = createContext<A11yContextType>({
  settings: defaultSettings,
  updateSettings: () => {},
  resetSettings: () => {},
  announce: () => {}
});

export const useA11y = () => useContext(A11yContext);

// Accessibility provider
interface A11yProviderProps {
  children: React.ReactNode;
}

export const A11yProvider: React.FC<A11yProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<A11ySettings>(() => {
    const saved = localStorage.getItem('a11y-settings');
    return saved ? JSON.parse(saved) : defaultSettings;
  });

  const [announcement, setAnnouncement] = useState<{
    message: string;
    priority: 'polite' | 'assertive';
  } | null>(null);

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Font size
    root.classList.remove('text-normal', 'text-large', 'text-x-large');
    root.classList.add(`text-${settings.fontSize}`);
    
    // Contrast
    root.classList.toggle('high-contrast', settings.contrast === 'high');
    
    // Reduced motion
    root.classList.toggle('reduced-motion', settings.reducedMotion);
    
    // Color blind modes
    root.setAttribute('data-color-blind', settings.colorBlindMode);
    
    // Save to localStorage
    localStorage.setItem('a11y-settings', JSON.stringify(settings));
  }, [settings]);

  const updateSettings = (updates: Partial<A11ySettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    setAnnouncement({ message, priority });
    setTimeout(() => setAnnouncement(null), 1000);
  };

  return (
    <A11yContext.Provider value={{ settings, updateSettings, resetSettings, announce }}>
      {children}
      
      {/* ARIA live region for announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement?.priority === 'polite' && announcement.message}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement?.priority === 'assertive' && announcement.message}
      </div>
    </A11yContext.Provider>
  );
};

// Accessibility panel component
interface AccessibilityPanelProps {
  isOpen: boolean;
  onClose: () => void;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

export const AccessibilityPanel: React.FC<AccessibilityPanelProps> = ({
  isOpen,
  onClose,
  position = 'bottom-right'
}) => {
  const { settings, updateSettings, resetSettings, announce } = useA11y();

  const positionClasses = {
    'bottom-right': 'bottom-20 right-4',
    'bottom-left': 'bottom-20 left-4',
    'top-right': 'top-20 right-4',
    'top-left': 'top-20 left-4'
  };

  if (!isOpen) return null;

  const handleChange = (key: keyof A11ySettings, value: string | number | boolean) => {
    updateSettings({ [key]: value });
    announce(`${key} set to ${value}`);
  };

  return createPortal(
    <div
      className={`fixed ${positionClasses[position]} z-50 w-80 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden`}
      role="dialog"
      aria-label="Accessibility settings"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold text-gray-800">Accessibility</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-gray-500 hover:bg-gray-200 rounded-lg"
          aria-label="Close accessibility panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Settings */}
      <div className="p-4 space-y-4">
        {/* Font size */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">
            <Type className="w-4 h-4 inline mr-2" />
            Text Size
          </label>
          <div className="flex gap-2">
            {(['normal', 'large', 'x-large'] as const).map((size) => (
              <button
                key={size}
                onClick={() => handleChange('fontSize', size)}
                className={`flex-1 py-2 text-sm rounded-lg transition-colors ${
                  settings.fontSize === size
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                aria-pressed={settings.fontSize === size}
              >
                {size === 'x-large' ? 'XL' : size.charAt(0).toUpperCase() + size.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Contrast */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">
            <Contrast className="w-4 h-4 inline mr-2" />
            Contrast
          </label>
          <div className="flex gap-2">
            {(['normal', 'high'] as const).map((level) => (
              <button
                key={level}
                onClick={() => handleChange('contrast', level)}
                className={`flex-1 py-2 text-sm rounded-lg transition-colors ${
                  settings.contrast === level
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                aria-pressed={settings.contrast === level}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Reduced motion */}
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">
            <Zap className="w-4 h-4 inline mr-2" />
            Reduce motion
          </label>
          <button
            onClick={() => handleChange('reducedMotion', !settings.reducedMotion)}
            className={`w-11 h-6 rounded-full transition-colors ${
              settings.reducedMotion ? 'bg-blue-600' : 'bg-gray-300'
            }`}
            role="switch"
            aria-checked={settings.reducedMotion}
          >
            <span
              className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                settings.reducedMotion ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Screen reader mode */}
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">
            <Volume2 className="w-4 h-4 inline mr-2" />
            Screen reader mode
          </label>
          <button
            onClick={() => handleChange('screenReaderMode', !settings.screenReaderMode)}
            className={`w-11 h-6 rounded-full transition-colors ${
              settings.screenReaderMode ? 'bg-blue-600' : 'bg-gray-300'
            }`}
            role="switch"
            aria-checked={settings.screenReaderMode}
          >
            <span
              className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                settings.screenReaderMode ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Color blind mode */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">
            Color blindness support
          </label>
          <select
            value={settings.colorBlindMode}
            onChange={(e) => handleChange('colorBlindMode', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="none">None</option>
            <option value="protanopia">Protanopia (red-blind)</option>
            <option value="deuteranopia">Deuteranopia (green-blind)</option>
            <option value="tritanopia">Tritanopia (blue-blind)</option>
          </select>
        </div>
      </div>

      {/* Reset button */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <button
          onClick={() => {
            resetSettings();
            announce('Settings reset to defaults');
          }}
          className="w-full py-2 text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Reset to defaults
        </button>
      </div>
    </div>,
    document.body
  );
};

// Floating accessibility button
interface AccessibilityButtonProps {
  position?: 'bottom-right' | 'bottom-left';
}

export const AccessibilityButton: React.FC<AccessibilityButtonProps> = ({
  position = 'bottom-right'
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4'
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed ${positionClasses[position]} z-40 w-12 h-12 bg-white border-2 border-gray-300 rounded-full shadow-lg flex items-center justify-center hover:border-blue-500 hover:shadow-xl transition-all`}
        aria-label="Open accessibility settings"
        title="Accessibility settings"
      >
        <Eye className="w-5 h-5 text-gray-700" />
      </button>

      <AccessibilityPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        position={position}
      />
    </>
  );
};

// Skip to content link
export const SkipToContent: React.FC<{ targetId?: string }> = ({
  targetId = 'main-content'
}) => (
  <a
    href={`#${targetId}`}
    className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg"
  >
    Skip to main content
  </a>
);

// Focus trap hook
export const useFocusTrap = (containerRef: React.RefObject<HTMLElement>, isActive: boolean) => {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    firstElement?.focus();

    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [containerRef, isActive]);
};

export default {
  A11yProvider,
  AccessibilityPanel,
  AccessibilityButton,
  SkipToContent,
  useA11y,
  useFocusTrap
};
